/**
 * useScrollPositionStore Hook
 *
 * Manages scroll position memory across channel/room changes.
 * When user switches to another channel and comes back, we restore their scroll position.
 *
 * Features:
 * - Stores position as "message ID + offset" (more reliable than pixel position)
 * - Session-based storage (clears on page refresh)
 * - Tracks if new messages arrived while user was away
 */

import { useRef, useCallback } from "react";

export interface ScrollPositionData {
  messageId: string;          // The message ID that was at the top of viewport
  offsetFromTop: number;      // Pixel offset from the top of that message
  savedAt: number;            // Timestamp when position was saved
  lastMessageId: string | null; // Last message ID when position was saved (to detect new messages)
  messageCount: number;       // Message count when position was saved
}

interface ScrollPositionStore {
  [sessionKey: string]: ScrollPositionData;
}

interface UseScrollPositionStoreResult {
  saveScrollPosition: (
    sessionKey: string,
    container: HTMLElement,
    messages: Array<{ messageId: string }>
  ) => void;

  getScrollPosition: (sessionKey: string) => ScrollPositionData | null;

  restoreScrollPosition: (
    sessionKey: string,
    container: HTMLElement,
    currentLastMessageId: string | null
  ) => { restored: boolean; hasNewMessages: boolean; newMessageCount: number };

  clearScrollPosition: (sessionKey: string) => void;

  clearAllPositions: () => void;
}

export function useScrollPositionStore(): UseScrollPositionStoreResult {
  // Use ref to persist across renders but reset on page refresh (session-based)
  const storeRef = useRef<ScrollPositionStore>({});

  /**
   * Save current scroll position for a session/room
   */
  const saveScrollPosition = useCallback((
    sessionKey: string,
    container: HTMLElement,
    messages: Array<{ messageId: string }>
  ) => {
    if (!sessionKey || !container || messages.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const scrollTop = container.scrollTop;

    // Find the first visible message (the one at the top of viewport)
    let topVisibleMessageId: string | null = null;
    let offsetFromTop = 0;

    // Iterate through message elements to find the first visible one
    const messageElements = container.querySelectorAll('[data-message-id]');

    for (const element of messageElements) {
      const rect = element.getBoundingClientRect();
      const messageId = element.getAttribute('data-message-id');

      // Check if this message is visible in viewport (top edge is within container)
      if (rect.top >= containerRect.top - 50 && rect.top <= containerRect.bottom) {
        topVisibleMessageId = messageId;
        // Calculate offset from the top of this message to the container top
        offsetFromTop = rect.top - containerRect.top;
        break;
      }

      // If message is partially visible from above
      if (rect.bottom > containerRect.top && rect.top < containerRect.top) {
        topVisibleMessageId = messageId;
        offsetFromTop = rect.top - containerRect.top; // Will be negative
        break;
      }
    }

    // Fallback: if no visible message found, use the first message
    if (!topVisibleMessageId && messages.length > 0) {
      topVisibleMessageId = messages[0].messageId;
      offsetFromTop = -scrollTop;
    }

    if (topVisibleMessageId) {
      const lastMessageId = messages.length > 0 ? messages[messages.length - 1].messageId : null;

      storeRef.current[sessionKey] = {
        messageId: topVisibleMessageId,
        offsetFromTop,
        savedAt: Date.now(),
        lastMessageId,
        messageCount: messages.length
      };

      console.log(`💾 Scroll position saved for ${sessionKey}:`, {
        messageId: topVisibleMessageId,
        offsetFromTop: offsetFromTop.toFixed(0),
        lastMessageId,
        messageCount: messages.length
      });
    }
  }, []);

  /**
   * Get saved scroll position for a session/room
   */
  const getScrollPosition = useCallback((sessionKey: string): ScrollPositionData | null => {
    return storeRef.current[sessionKey] || null;
  }, []);

  /**
   * Restore scroll position for a session/room
   * Returns info about whether restoration was successful and if new messages arrived
   */
  const restoreScrollPosition = useCallback((
    sessionKey: string,
    container: HTMLElement,
    currentLastMessageId: string | null
  ): { restored: boolean; hasNewMessages: boolean; newMessageCount: number } => {
    const savedPosition = storeRef.current[sessionKey];

    if (!savedPosition || !container) {
      return { restored: false, hasNewMessages: false, newMessageCount: 0 };
    }

    // Find the anchor message element
    const anchorElement = document.getElementById(`message-${savedPosition.messageId}`);

    if (!anchorElement) {
      console.log(`⚠️ Anchor message not found: ${savedPosition.messageId}`);
      // Message might have been deleted or not loaded yet
      // Clear the saved position as it's no longer valid
      delete storeRef.current[sessionKey];
      return { restored: false, hasNewMessages: false, newMessageCount: 0 };
    }

    // Calculate where we need to scroll to restore the same view
    const containerRect = container.getBoundingClientRect();
    const anchorRect = anchorElement.getBoundingClientRect();

    // Current offset of anchor from container top
    const currentOffset = anchorRect.top - containerRect.top;

    // How much we need to adjust scroll to match saved offset
    const scrollAdjustment = currentOffset - savedPosition.offsetFromTop;

    // Apply the scroll adjustment
    container.scrollTop = container.scrollTop + scrollAdjustment;

    // Check if new messages arrived while user was away
    const hasNewMessages = currentLastMessageId !== null &&
                          savedPosition.lastMessageId !== null &&
                          currentLastMessageId !== savedPosition.lastMessageId;

    // Estimate new message count (rough estimate, actual count may vary)
    const newMessageCount = hasNewMessages ? 1 : 0; // We can't know exact count without comparing arrays

    console.log(`📍 Scroll position restored for ${sessionKey}:`, {
      anchorId: savedPosition.messageId,
      adjustment: scrollAdjustment.toFixed(0),
      hasNewMessages,
      savedLastMessageId: savedPosition.lastMessageId,
      currentLastMessageId
    });

    return { restored: true, hasNewMessages, newMessageCount };
  }, []);

  /**
   * Clear saved scroll position for a specific session/room
   */
  const clearScrollPosition = useCallback((sessionKey: string) => {
    delete storeRef.current[sessionKey];
    console.log(`🗑️ Scroll position cleared for ${sessionKey}`);
  }, []);

  /**
   * Clear all saved scroll positions
   */
  const clearAllPositions = useCallback(() => {
    storeRef.current = {};
    console.log(`🗑️ All scroll positions cleared`);
  }, []);

  return {
    saveScrollPosition,
    getScrollPosition,
    restoreScrollPosition,
    clearScrollPosition,
    clearAllPositions
  };
}
